/**
 * Lazy-render sections that are far below the fold.
 *
 * Marking images loading="lazy" is not enough: it is a hint the browser is
 * free to ignore, and on this store it does. Measured on the juice cleanse
 * PDP, 80 images totalling 6.6 MB sat 1250-3000px below the fold, 49 of them
 * marked lazy, and every one downloaded before the user scrolled at all.
 *
 * An <img> inside an inert <template> is not a request the browser can make,
 * so this is deterministic rather than advisory.
 *
 * Hydration happens on whichever comes first:
 *   1. IntersectionObserver, 800px ahead of the viewport, for real users.
 *   2. An idle callback after window load, as a backstop.
 *
 * The backstop is not optional. Google Search does not scroll ("Google Search
 * does not interact with your page"), and neither does find-in-page or a
 * screen reader's virtual cursor. Hydrating at idle guarantees the products
 * and their links reach the rendered DOM regardless of scrolling, while still
 * keeping them out of the window where they would compete with LCP.
 */
(function () {
  'use strict';

  if (window.__ctrLazySection) return;

  var ROOT_MARGIN = '800px 0px';
  var IDLE_TIMEOUT = 3000;

  function hydrate(el) {
    if (!(el instanceof Element)) return false;
    // Clear the flag FIRST so the observer and the idle backstop cannot both clone.
    if (!el.hasAttribute('data-ctr-lazy')) return false;
    el.removeAttribute('data-ctr-lazy');

    var tpl = el.querySelector('template[data-ctr-list-template]');
    if (!tpl) {
      // R66: a normal page render carries no template - the cards are rendered only when this
      // section is fetched through the Section Rendering API (sections/product-list.liquid
      // skips the card loop when section.index is set). Fetch it now.
      var sectionId = el.getAttribute('data-ctr-section-id');
      if (!sectionId) return false;
      fetchSection(el, sectionId);
      return true;
    }

    var frag = tpl.content.cloneNode(true);
    tpl.remove();
    el.appendChild(frag);

    // Custom elements upgrade themselves on insertion. Alpine does not -- it
    // never re-scans nodes added after its own init pass, so the quick-add
    // form inside each card would render but do nothing.
    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      try {
        window.Alpine.initTree(el);
      } catch (err) {
        /* non-fatal */
      }
    }

    try {
      el.dispatchEvent(new CustomEvent('ctr:lazy-section:hydrated', { bubbles: true }));
    } catch (err) {
      /* non-fatal */
    }

    return true;
  }

  /**
   * R66: fetch the section's own markup and move the rendered cards into the page. The
   * response is the same section rendered with section.index nil, so it carries the
   * <template data-ctr-list-template> the inline path used to find in the document, plus
   * the mobile carousel copy when the section is configured with one. Runs once per section;
   * on failure the visually-hidden link list is revealed so every product stays reachable.
   */
  function fetchSection(el, sectionId) {
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId);
    var links = el.querySelector('[data-ctr-list-links]');
    var reveal = function () {
      if (links) links.classList.remove('visually-hidden');
    };
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var remote = doc.querySelector('[data-ctr-lazy-section]');
        var tpl = remote && remote.querySelector('template[data-ctr-list-template]');
        if (!tpl) {
          reveal();
          return;
        }
        if (links) links.remove();
        el.appendChild(document.importNode(tpl.content, true));

        // Codex R-FTR finding 1 (12 Sep 2026): a Section Rendering response may carry
        // <style data-section-stylesheet> holding the subsetted {% stylesheet %} rules of the
        // blocks it renders, when Shopify's CSS subsetting applies to the theme. On this theme
        // it does not and cannot (ledger, 7 Sep: eligibility is fixed at install; the fetched
        // footer carries zero such tags and every block rule is in the one global styles.css),
        // so this branch is inert today. It is here so the fetched blocks keep their styles on
        // any theme where the platform does subset.
        var remoteStyles = doc.querySelectorAll('style[data-section-stylesheet]');
        for (var i = 0; i < remoteStyles.length; i++) {
          var key = remoteStyles[i].getAttribute('data-section-stylesheet') || sectionId;
          if (!document.head.querySelector('style[data-section-stylesheet="' + key + '"]')) {
            var st = document.importNode(remoteStyles[i], true);
            st.setAttribute('data-section-stylesheet', key);
            document.head.appendChild(st);
          }
        }

        // A grid section with "carousel on mobile" renders its mobile copy as a sibling of
        // the list, outside the template. Bring it along when the page does not have one.
        var mobile = doc.querySelector('.resource-list.hidden--desktop');
        if (mobile && el.parentNode && !el.parentNode.querySelector('.resource-list.hidden--desktop')) {
          el.insertAdjacentElement('afterend', document.importNode(mobile, true));
        }

        if (window.Alpine && typeof window.Alpine.initTree === 'function') {
          try {
            window.Alpine.initTree(el.parentNode || el);
          } catch (err) {
            /* non-fatal */
          }
        }
        try {
          el.dispatchEvent(new CustomEvent('ctr:lazy-section:hydrated', { bubbles: true }));
        } catch (err) {
          /* non-fatal */
        }
      })
      .catch(reveal);
  }

  function watch(el) {
    if (!('IntersectionObserver' in window)) {
      hydrate(el);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            hydrate(el);
            return;
          }
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
    io.observe(el);
  }

  function backstop(el) {
    var run = function () {
      hydrate(el);
    };
    var schedule = function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT });
      } else {
        setTimeout(run, IDLE_TIMEOUT);
      }
    };
    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-ctr-lazy-section][data-ctr-lazy]');
    for (var i = 0; i < nodes.length; i++) {
      watch(nodes[i]);
      backstop(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // The theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  window.__ctrLazySection = { hydrate: hydrate, init: init };
})();
